import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Space, message, DatePicker, Tooltip } from 'antd';
import { WarningOutlined, TableOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MainLayout from '../components/MainLayout';
import ItemDetailDrawer from '../components/ItemDetailDrawer';
import { itemService } from '../services/items';
import { documentService } from '../services/documents';
import { tenantService } from '../services/tenants';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { downloadXlsx } from '../utils/excelExport';
import './Dashboard.css';

const { Title } = Typography;

function Workers() {
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [missingDocsByItemId, setMissingDocsByItemId] = useState({});
  const [tenantMap, setTenantMap] = useState({});
  const [tableSort, setTableSort] = useState(null);
  const [form] = Form.useForm();
  const { selectedTenantId } = useTenant();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canMutateItems = user?.role !== 'all_external';

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { item_type: 'worker' };
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      const res = await itemService.list('worker', params);
      const mapped = (res || []).map((item) => ({
        ...item,
        first_name: item.standard_fields?.first_name,
        last_name: item.standard_fields?.last_name,
        fiscal_code: item.standard_fields?.fiscal_code,
        hire_date: item.standard_fields?.hire_date,
        job_role: item.standard_fields?.job_role,
        birth_date: item.standard_fields?.birth_date,
        birth_place: item.standard_fields?.birth_place,
      }));
      setData(mapped);
      checkMissingDocs(mapped);
    } catch (e) {
      message.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const loadTenants = useCallback(async () => {
    const canLoadTenants = user?.role === 'admin' || user?.role === 'qhse_nke' || user?.role === 'all_external';
    if (!canLoadTenants) return;
    try {
      const res = await tenantService.list();
      const map = {};
      (res || []).forEach((t) => {
        map[t.id] = t.name;
      });
      setTenantMap(map);
    } catch (e) {
      console.error('Errore caricamento tenant', e);
    }
  }, [user]);

  const checkMissingDocs = async (items) => {
    const missingMap = {};
    const requiredDocs = ['Carta d\'identità', 'UNILAV', 'Idoneità medica', 'Formazione base e specifica', 'Consegna DPI', 'Fototessera'];
    
    for (const item of items) {
      try {
        const docs = await documentService.list({ item_id: item.id });
        const loadedDocTypes = docs.map(d => d.document_type);
        const missing = requiredDocs.filter(reqDoc => !loadedDocTypes.includes(reqDoc));
        if (missing.length > 0) {
          missingMap[item.id] = missing;
        }
      } catch (e) {
        console.error(`Errore caricamento documenti per item ${item.id}:`, e);
      }
    }
    setMissingDocsByItemId(missingMap);
  };

  useEffect(() => {
    loadWorkers();
    loadTenants();
  }, [selectedTenantId, loadWorkers, loadTenants]);

  const dataWithTenant = useMemo(() => {
    return (data || []).map((item) => ({
      ...item,
      company_name: tenantMap[item.tenant_id] || '',
    }));
  }, [data, tenantMap]);

  const filteredData = useMemo(() => {
    if (!search) return dataWithTenant;
    const term = search.toLowerCase();
    return dataWithTenant.filter((item) => Object.values(item || {}).some((val) => (val !== null && val !== undefined) && String(val).toLowerCase().includes(term)));
  }, [dataWithTenant, search]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const dateSorter = (key) => (a, b) => (a[key] ? new Date(a[key]).getTime() : 0) - (b[key] ? new Date(b[key]).getTime() : 0);

  const sortComparators = {
    id: (a, b) => a.id - b.id,
    company_name: stringSorter('company_name'),
    first_name: stringSorter('first_name'),
    last_name: stringSorter('last_name'),
    fiscal_code: stringSorter('fiscal_code'),
    job_role: stringSorter('job_role'),
    hire_date: dateSorter('hire_date'),
  };

  const sortedData = useMemo(() => {
    if (!tableSort?.order || !tableSort?.field || !sortComparators[tableSort.field]) {
      return filteredData;
    }
    const arr = [...filteredData];
    arr.sort(sortComparators[tableSort.field]);
    if (tableSort.order === 'descend') arr.reverse();
    return arr;
  }, [filteredData, tableSort]);

  const handleTableChange = (_, __, sorter) => {
    if (sorter && sorter.order) {
      setTableSort({ field: sorter.field || sorter.columnKey, order: sorter.order });
    } else {
      setTableSort(null);
    }
  };

  const handleExportXlsx = () => {
    const headers = ['ID', 'Azienda', 'Nome', 'Cognome', 'CF', 'Mansione', 'Data Assunzione', 'Attivo'];
    const rows = (sortedData || []).map((item) => [
      item.id,
      item.company_name || '',
      item.first_name || '',
      item.last_name || '',
      item.fiscal_code || '',
      item.job_role || '',
      item.hire_date ? dayjs(item.hire_date).format('DD/MM/YYYY') : '',
      item.is_active ? 'Sì' : 'No',
    ]);
    downloadXlsx('lavoratori.xlsx', headers, rows);
  };

  const onSubmit = async (values) => {
    try {
      const standard_fields = {
        first_name: values.first_name,
        last_name: values.last_name,
        fiscal_code: values.fiscal_code,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
        job_role: values.job_role,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
        birth_place: values.birth_place,
      };

      const payload = {
        item_type: 'worker',
        standard_fields,
        is_active: values.is_active ?? true,
      };
      
      // Aggiungi tenant_id se selezionato
      if (selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }

      if (editing) {
        await itemService.update(editing.id, payload);
        message.success('Lavoratore aggiornato');
      } else {
        await itemService.create(payload);
        message.success('Lavoratore creato');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadWorkers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      first_name: record.first_name,
      last_name: record.last_name,
      fiscal_code: record.fiscal_code,
      hire_date: record.hire_date ? dayjs(record.hire_date) : null,
      job_role: record.job_role,
      birth_date: record.birth_date ? dayjs(record.birth_date) : null,
      birth_place: record.birth_place,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleToggle = async (record) => {
    try {
      await itemService.update(record.id, { is_active: !record.is_active });
      message.success(record.is_active ? 'Lavoratore disattivato' : 'Lavoratore attivato');
      loadWorkers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleRowClick = (record) => {
    setSelectedItem(record);
    setDrawerOpen(true);
  };

  const handleBulkDisable = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => itemService.update(id, { is_active: false })));
      message.success('Lavoratori disattivati con successo');
      setSelectedRowKeys([]);
      loadWorkers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: 'Confermi la rimozione bulk dei lavoratori selezionati?',
      okType: 'danger',
      okText: 'Rimuovi',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await itemService.bulkDelete(selectedRowKeys);
          message.success('Lavoratori rimossi con successo');
          setSelectedRowKeys([]);
          loadWorkers();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const rowSelection = isAdmin ? {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  } : null;

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, sorter: (a, b) => a.id - b.id, sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'id' ? tableSort.order : null },
    { title: 'Azienda', dataIndex: 'company_name', key: 'company_name', sorter: stringSorter('company_name'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'company_name' ? tableSort.order : null },
    {
      title: 'Nome',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: stringSorter('first_name'),
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'first_name' ? tableSort.order : null,
      render: (text, record) => (
        <Space>
          {missingDocsByItemId[record.id] && (
            <Tooltip title={`Documenti mancanti: ${missingDocsByItemId[record.id].join(', ')}`}>
              <WarningOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
            </Tooltip>
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    { title: 'Cognome', dataIndex: 'last_name', key: 'last_name', sorter: stringSorter('last_name'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'last_name' ? tableSort.order : null },
    { title: 'CF', dataIndex: 'fiscal_code', key: 'fiscal_code', sorter: stringSorter('fiscal_code'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'fiscal_code' ? tableSort.order : null },
    { title: 'Mansione', dataIndex: 'job_role', key: 'job_role', sorter: stringSorter('job_role'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'job_role' ? tableSort.order : null },
    {
      title: 'Data Assunzione',
      dataIndex: 'hire_date',
      key: 'hire_date',
      sorter: dateSorter('hire_date'),
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'hire_date' ? tableSort.order : null,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : ''),
    },
    { title: 'Attivo', dataIndex: 'is_active', key: 'is_active', render: (v) => (v ? 'Sì' : 'No') },
    ...(canMutateItems ? [{
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={(e) => { e.stopPropagation(); handleEdit(record); }}>Modifica</Button>
          <Button size="small" onClick={(e) => { e.stopPropagation(); handleToggle(record); }}>
            {record.is_active ? 'Disattiva' : 'Attiva'}
          </Button>
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout title="Lavoratori">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Lavoratori</Title>
          <Button icon={<TableOutlined />} onClick={handleExportXlsx}>Esporta Excel</Button>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Space>
            {isAdmin && selectedRowKeys.length > 0 && (
              <Button danger type="primary" onClick={handleBulkDelete}>
                Elimina selezionati ({selectedRowKeys.length})
              </Button>
            )}
            {canMutateItems && (
              <Button type="primary" size="large" onClick={() => setModalOpen(true)}>+ Nuovo Lavoratore</Button>
            )}
          </Space>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sortedData}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} lavoratori`,
          }}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={editing ? 'Modifica Lavoratore' : 'Nuovo Lavoratore'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editing ? 'Salva' : 'Crea'}
        cancelText="Annulla"
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="first_name" label="Nome" rules={[{ required: true, message: 'Inserisci il nome' }]} style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Mario" />
            </Form.Item>
            <Form.Item name="last_name" label="Cognome" rules={[{ required: true, message: 'Inserisci il cognome' }]} style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Rossi" />
            </Form.Item>
          </Space>

          <Form.Item name="fiscal_code" label="Codice Fiscale" rules={[{ required: true, message: 'Inserisci CF' }]}>
            <Input placeholder="RSSMRA80A01H501U" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="hire_date" label="Data Assunzione" style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="job_role" label="Mansione" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Operaio" />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="birth_date" label="Data Nascita" style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="birth_place" label="Luogo Nascita" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Milano" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <ItemDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={selectedItem}
        itemType="worker"
      />
    </MainLayout>
  );
}

export default Workers;
